import React, { ReactElement, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Button, Pagination } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { AppCriteriaField, AppResultInfo, AppSkinProps, ResultTemplates } from '../../../../types/decisions';
import { getFieldName, transformCriteriaData } from '../../../../utils';
import FavoriteWithCenteredNumber from '../../../favorite-with-number/FavoriteWithNumber';
import ScrollShadowBox from '../../../ScrollShadowBox/ScrollShadowBox';
import { chooseTreatment, getPatientInfo } from '../../../../api/patient.api';
import ConfirmModal from '../../../ConfirmModal/ConfirmModal';
import { useAuth } from '../../../../context/auth-context';
import './AppResults.scss';

interface AppResultsProps {
    results: AppSkinProps['results'];
    onRerank: AppSkinProps['onRerank'];
    onRerankMany: AppSkinProps['onRerankMany'];
    currentPage: number;
    shouldShowRankByManyCheckbox?: boolean;
    selectedProductIds: AppSkinProps['selectedProductIds'];
    setSelectedProductIds: AppSkinProps['setSelectedProductIds'];
    allowedFieldsIds: string[];
    criteriaData?: AppSkinProps['criteriaData'];
    criteriaConstraints?: AppSkinProps['criteriaConstraints'];
    chosenProductId?: string;
}

const renderSpecByType = (value: AppCriteriaField['val'], type: AppCriteriaField['type']): ReactElement => {
    if (value === null) {
        return <div>-</div>;
    }

    switch (type) {
        case 'select':
        case 'multiselect':
        case 'bool':
            return (
                <div>
                    {typeof value === 'object' ?
                        Object.keys(value).map((key) => {
                            // multiselect not checked case
                            if (value[key] === '-1') {
                                return null;
                            }

                            return (
                                <div className="multi-spec-item" key={key + value[key]}>
                                    {key} ({value[key]})
                                </div>
                            );
                        }) : (
                            value
                        )
                    }
                </div>
            );
        case 'color':
            return (
                <div>
                    {typeof value === 'object' ? (
                        <div className="new-results-product-specs-spec-colors">
                            {Object.keys(value).map((key) => (
                                <div
                                    key={key + value[key]}
                                    className="new-results-product-specs-spec-color"
                                    style={{ backgroundColor: value[key] }}
                                />
                            ))}
                        </div>
                    ) : (
                        <div
                            className="new-results-product-specs-spec-color"
                            style={{ backgroundColor: value }}
                        />
                    )
                    }
                </div>
            );
        case 'number':
        case 'text':
        case 'dynamic':
        case 'date':
        case 'zipcode':
            return <div>{value as string}</div>;
        default:
            return <div>-</div>;
    }
};

export const ResultProductCardSpecs = ({
    id,
    nonTechnicalFields,
    allowedFieldsIds,
}: {
    id: AppResultInfo['id'],
    nonTechnicalFields: AppResultInfo['nonTechnicalFields'],
    allowedFieldsIds: string[],
}) => {

    return (
        <div className="new-results-product-specs">
            {nonTechnicalFields.map(({
                id: itemId,
                value,
                name,
                type,
            }) => {
                if (!allowedFieldsIds.includes(itemId)) {
                    return null;
                }

                return (
                    <div
                        className="new-results-product-specs-spec"
                        key={id + itemId}
                    >
                        <div>{getFieldName(name)}</div>
                        {renderSpecByType(value, type)}
                    </div>
                );
            })}
        </div>
    );
};

type ProductAction = {
    productId: string;
    name: string;
    link: string
}

const ProductResults = ({
    results,
    onRerank,
    onRerankMany,
    currentPage,
    shouldShowRankByManyCheckbox = false,
    selectedProductIds,
    setSelectedProductIds,
    allowedFieldsIds,
    criteriaData,
    criteriaConstraints,
    chosenProductId,
}: AppResultsProps): React.JSX.Element => {
    const [ searchParams ] = useSearchParams();
    const dataset = searchParams.get('dataset') || 'main';
    const [ selectedAction, setSelectedAction ] = useState<ProductAction | null>(null);
    const [ confirmModalOpen, setConfirmModalOpen ] = useState(false);
    const [ treatmentSelectionLoading, setTreatmentSelectionLoading ] = useState(false);

    const user = JSON.parse(localStorage.getItem('user') || '{"isOrganic":false}');
    const isOrganicUser = user.isOrganic || false;

    const onProductActionClick = (productId: string, actionName: string, actionLink: string) => {
        setSelectedAction({
            productId,
            name: actionName,
            link: actionLink,
        });
        setConfirmModalOpen(true);
    };

    const onProductActionConfirm = () => {
        if (!selectedAction || !criteriaData || !criteriaConstraints) {
            setConfirmModalOpen(false);
            return;
        }

        const { criteria, constraints } = transformCriteriaData(criteriaData, criteriaConstraints);
        setTreatmentSelectionLoading(true);
        chooseTreatment({
            appId: criteriaData.id,
            datasetId: dataset,
            treatmentId: selectedAction.productId,
            criteria,
            constraints,
        })
        .then(() => {
            setConfirmModalOpen(false);
            if (isOrganicUser) {
                return;
            }
            window.open(selectedAction.link, '_blank');
            window.location.reload();
        }).finally(() => setTreatmentSelectionLoading(false));
    };

    return (
        <>
            {results.map((result, index) => {
                const name = getFieldName(result.data.name);
                const productLink = result.data.productlink?.value;
                const actionLink = result.data.actionlink?.value;
                const actionCTAName = result.data.actionbuttontext?.value;
                let shouldDisplayInferButton = true;
                if (index === 0 && currentPage === 1) {
                    shouldDisplayInferButton = false;
                }

                if (chosenProductId) {
                    shouldDisplayInferButton = false;
                }

                return (
                    <div
                        className={`new-results-item ${chosenProductId === result.id ? 'chosen-item' : ''}`}
                        key={result.id}
                    >
                        {shouldShowRankByManyCheckbox && (
                            <div style={{
                                position: 'absolute',
                                left: 8,
                                top: 8,
                            }}>
                                <div style={{
                                    marginLeft: -3,
                                    marginTop: -3,
                                }}>
                                    <FavoriteWithCenteredNumber
                                        count={selectedProductIds.indexOf(result.id) + 1}
                                        isLiked={selectedProductIds.includes(result.id)}
                                        onClick={(isChecked) => {
                                            if (isChecked) {
                                                setSelectedProductIds([ ...selectedProductIds, result.id ]);
                                            } else {
                                                setSelectedProductIds(selectedProductIds.filter(id => id !== result.id));
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                        <div className="image-container" style={{ backgroundImage: `url('${result.icon}')` }}/>
                        <div className="new-results-item-details">
                            <div className="new-results-item-details-rank">
                                <div className="new-results-item-details-rank-bar"
                                     style={{ width: result.rank + '%' }}/>
                                <div className="new-results-item-details-rank-rate">
                                    {result.rank === '-' ? <>&nbsp;</> : result.rank + '%'}
                                </div>
                            </div>
                            <div>
                                <h3
                                    className={`truncate ${productLink ? 'product-link' : ''}`}
                                    title={productLink || name}
                                    onClick={() => productLink && window.open(productLink, '_blank')}
                                >
                                    {name}
                                </h3>
                                <ResultProductCardSpecs
                                    id={result.id}
                                    nonTechnicalFields={result.nonTechnicalFields}
                                    allowedFieldsIds={allowedFieldsIds}
                                />
                            </div>
                            <div className="new-results-item-details-ctas">
                                {shouldDisplayInferButton && (
                                    <Button
                                        className="new-results-item-details-ctas-cta"
                                        variant="outlined"
                                        onClick={() => onRerank(result.id, dataset, true)}
                                        startIcon={<ArrowUpwardIcon/>}
                                    />
                                )}
                                {actionLink && actionCTAName && !criteriaData?.disabled && (
                                    <Button
                                        className="new-results-item-details-ctas-cta action-link"
                                        variant="outlined"
                                        onClick={() => onProductActionClick(result.id, actionCTAName, actionLink)}
                                    >
                                        {isOrganicUser ? 'I choose this treatment' : actionCTAName}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}

            <Button
                onClick={() => onRerankMany(dataset, true)}
                style={{
                    zIndex: 9999,
                    position: 'fixed',
                    bottom: 16,
                    right: selectedProductIds.length > 0 ? 16 : -100,
                    transition: 'right 0.3s ease-in-out',
                    backgroundColor: 'white',
                }}
                variant="outlined"
            >
                Infer
            </Button>
            <ConfirmModal
                isLoading={treatmentSelectionLoading}
                open={confirmModalOpen}
                title="Are you sure you want to continue?"
                message="The treatment you selected will be shared with your doctor."
                onConfirm={onProductActionConfirm}
                onCancel={() => setConfirmModalOpen(false)}
            />
        </>
    );
};

const ThreeColumns = ({
    results,
    onRerank,
    onRerankMany,
    currentPage,
    shouldShowRankByManyCheckbox,
    selectedProductIds,
    setSelectedProductIds,
    allowedFieldsIds,
    criteriaData,
    criteriaConstraints,
    chosenProductId,
}: AppResultsProps) => {
    return (
        <div className="threeColumns">
            <ProductResults
                results={results}
                onRerank={onRerank}
                onRerankMany={onRerankMany}
                currentPage={currentPage}
                shouldShowRankByManyCheckbox={shouldShowRankByManyCheckbox}
                selectedProductIds={selectedProductIds}
                setSelectedProductIds={setSelectedProductIds}
                allowedFieldsIds={allowedFieldsIds}
                criteriaData={criteriaData}
                criteriaConstraints={criteriaConstraints}
                chosenProductId={chosenProductId}
            />
        </div>
    );
};

const LargeProductCards = ({
    results,
    onRerank,
    onRerankMany,
    currentPage,
    shouldShowRankByManyCheckbox,
    selectedProductIds,
    setSelectedProductIds,
    allowedFieldsIds,
    criteriaData,
    criteriaConstraints,
    chosenProductId,
}: AppResultsProps) => {
    return (
        <div className="largeProducts">
            <ProductResults
                results={results}
                onRerank={onRerank}
                onRerankMany={onRerankMany}
                currentPage={currentPage}
                shouldShowRankByManyCheckbox={shouldShowRankByManyCheckbox}
                selectedProductIds={selectedProductIds}
                setSelectedProductIds={setSelectedProductIds}
                allowedFieldsIds={allowedFieldsIds}
                criteriaData={criteriaData}
                criteriaConstraints={criteriaConstraints}
                chosenProductId={chosenProductId}
            />
        </div>
    );
};

const AppResults = ({
    pageInfo,
    results,
    onPageChange,
    onRerank,
    onRerankMany,
    shouldShowRankByManyCheckbox,
    template = '3columns',
    selectedProductIds,
    setSelectedProductIds,
    allowedFieldsIds,
    criteriaData,
    criteriaConstraints,
}: Omit<AppResultsProps, 'currentPage'> & {
    template?: ResultTemplates;
    pageInfo: AppSkinProps['pageInfo'];
    onPageChange: AppSkinProps['onPageChange'];
    criteriaData?: AppSkinProps['criteriaData'];
    criteriaConstraints?: AppSkinProps['criteriaConstraints'];
}) => {
    const [ searchParams ] = useSearchParams();
    const location = useLocation();
    const dataset = searchParams.get('dataset') || 'main';
    const treatmentId = searchParams.get('treatmentId') || '0';
    const { userInfo: authUserInfo } = useAuth();
    const [ userChoice, setUserChoice ] = useState<AppResultInfo | null>(null);

    useEffect(() => {
        if (!authUserInfo) {
            return;
        }

        if (authUserInfo.role === 'patient') {
            getPatientInfo()
            .then((response) => {
                const foundResultInfo = response.find(item => {
                    return item.appId + '' === criteriaData?.id
                        && item.datasetId + '' === dataset
                        && item.datasetId;
                });

                if (!foundResultInfo) {
                    return;
                }

                const result = results.find(item => item.id === foundResultInfo.treatmentId);
                if (!result) {
                    return;
                }

                setUserChoice(result);
            });
        } else if ([ 'doctor', 'manager' ].includes(authUserInfo.role)) {
            const result = results.find(item => item.id === treatmentId);
            if (!result) {
                return;
            }
            setUserChoice(result);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ criteriaData?.id, dataset, results ]);

    if (results.length === 0) {
        return (
            <div className="no-results">
                No products found.
            </div>
        );
    }

    let ResultTemplate = ThreeColumns;

    if (template === 'largeProducts') {
        ResultTemplate = LargeProductCards;
    }

    const showPatientChoice = location.pathname.startsWith('/patient-choice');

    return (
        <div className="new-app-results-container">
            <h3>
                <span>Results ({pageInfo.total})</span>

                {userChoice && showPatientChoice && (
                    <span className="user-choice">
                        Your choice is <span>{getFieldName(userChoice.data.name)}</span>
                    </span>
                )}
            </h3>
            <div className="new-app-results">
                <ScrollShadowBox>
                    <ResultTemplate
                        results={results}
                        onRerank={onRerank}
                        onRerankMany={onRerankMany}
                        currentPage={pageInfo.current_page}
                        shouldShowRankByManyCheckbox={shouldShowRankByManyCheckbox}
                        selectedProductIds={selectedProductIds}
                        setSelectedProductIds={setSelectedProductIds}
                        allowedFieldsIds={allowedFieldsIds}
                        criteriaData={criteriaData}
                        criteriaConstraints={criteriaConstraints}
                        chosenProductId={showPatientChoice ? userChoice?.id : ''}
                    />
                </ScrollShadowBox>
            </div>
            <div className="app-results-page-info">
                {/*Displayed products: <b>{pageInfo.start}</b> to <b>{pageInfo.end}</b> of <b>{pageInfo.total}</b>.*/}
                <Pagination
                    page={pageInfo.current_page}
                    count={pageInfo.total_pages}
                    shape="rounded"
                    onChange={(_, page) => onPageChange(dataset, page)}
                />
            </div>
        </div>
    );
};

export default AppResults;